import React, { useEffect, useState } from 'react';
import { CircularProgress, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

/**
 * A reusable search and select dropdown component built on Material-UI's Autocomplete.
 * @param {object} props
 * @param {boolean} [props.disablePortal=false] - Crucial for modal compatibility. Set this to `true`
 *   when this component is rendered inside a modal from a different library (like Shadcn/Radix).
 *   The Autocomplete dropdown uses a "portal" by default, which can cause non-MUI modals
 *   to incorrectly intercept click events and close. Disabling the portal ensures the dropdown
 *   remains within the host modal's event context, preventing this issue.
 */
function SearchableSelect({
    disablePortal = false,
    initialValue = null,
    label = 'Select an Option',
    loading,
    onChange,
    options,
}) {
    const [value, setValue] = useState(null);

    useEffect(() => {
        if (!(onChange instanceof Function)) {
            console.error('SearchableSelect intialized incorrectly onChange is not a function');
        }
    }, [onChange]);

    useEffect(() => {
        if (!loading && initialValue && options.length > 0) {
            const initialOption = options.find((option) => option.value === initialValue);
            if (initialOption) {
                setValue(initialOption);
            }
        }
    }, [loading, initialValue, options]);

    useEffect(() => {
        onChange(value);
    }, [value, onChange]);

    return (
        <Autocomplete
            disablePortal={disablePortal}
            options={options}
            getOptionLabel={(option) => option.label}
            getOptionSelected={(option, value) => option.value === value?.value}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            value={value}
            onChange={(event, newValue) => setValue(newValue)}
        />
    );
}

export { SearchableSelect };
